import React, { Dispatch, useCallback, useContext, useEffect, useState } from "react";
import { useAISLayer } from "../../fish-map/layers/ais";
import { OceanMask } from "../../fish-map/layers/ocean-mask";
import { getTooltip } from "../picked-object-view";
import { PickInfo, ViewStateProps } from "@deck.gl/core/lib/deck";
import "../fish-map/FilterMenu.css";
import { Page } from "..";
import RouteMenu, { defaultRouteInput, useRouteInput } from "./RouteMenu";
import { useFacilitiesLayer } from "../../fish-map/layers/facilities";
import { Layer } from "@deck.gl/core";
import { getRouteLayer, Route } from "../../fish-map/layers/route";
import { DarkModeContext } from "../../darkModeContext";
import { getLocationHighlight } from "../../fish-map/layers/highlight-point";
import { useLocation } from "react-router-dom";
import { useHeatmapGridLayer } from "../../fish-map/layers/heat-map-grid";
import { Species } from "../../types";
import { useTranslation } from "../../i18n";

export function RoutePlanner(_setLoading: Dispatch<boolean>, setViewState: Dispatch<ViewStateProps | undefined>): Page {

    const [darkMode] = useContext(DarkModeContext);
    const [routeInput, setRouteInput] = useRouteInput();
    const [aisLayer, aisVessels] = useAISLayer(true);
    const facilitiesLayer = useFacilitiesLayer();

    const [routeData, setRouteData] = useState<Route | undefined>();

    useEffect(() => {
        if (!(routeInput.voyage.start?.lat && routeInput.voyage.start?.lng) &&
            !(routeInput.voyage.stop?.lat && routeInput.voyage.stop?.lng))
            return;

        let newViewState = {};

        if (routeInput.voyage.start?.lat && routeInput.voyage.start?.lng) {
            newViewState = {
                latitude: routeInput.voyage.start.lat,
                longitude: routeInput.voyage.start.lng,
                zoom: 8
            };
        } else if (routeInput.voyage.stop?.lat && routeInput.voyage.stop?.lng) {
            newViewState = {
                latitude: routeInput.voyage.stop.lat,
                longitude: routeInput.voyage.stop.lng,
                zoom: 8
            };
        }

        if (routeInput.voyage.start?.lat && routeInput.voyage.start?.lng &&
            routeInput.voyage.stop?.lat && routeInput.voyage.stop?.lng) {
                newViewState = {
                    latitude: (routeInput.voyage.start.lat + routeInput.voyage.stop.lat) / 2,
                    longitude: (routeInput.voyage.start.lng + routeInput.voyage.stop.lng) / 2,
                    zoom: 7
                };
            }

        setViewState({...newViewState, pitch: 40});

    }, [routeInput.voyage.start?.lat, routeInput.voyage.start?.lng, routeInput.voyage.stop?.lat, routeInput.voyage.stop?.lng, setViewState])

    const heatmapLayer = useHeatmapGridLayer(1, routeInput.voyage.preferredSpecies[0], useCallback(() => undefined, []), !routeData);

    const layers: Layer<any>[] = [
        OceanMask.getOceanMaskLayer(),
        heatmapLayer
    ];

    const highlightPoints = [];
    if (routeInput.voyage.start?.lat && routeInput.voyage.start?.lng) {
        highlightPoints.push({ type: "Feature", geometry: { coordinates: [routeInput.voyage.start.lng, routeInput.voyage.start.lat], type: "Point" }});   
    }
    if (routeInput.voyage.stop?.lat && routeInput.voyage.stop?.lng) {
        highlightPoints.push({ type: "Feature", geometry: { coordinates: [routeInput.voyage.stop.lng, routeInput.voyage.stop.lat], type: "Point" }});
    }

    if (highlightPoints.length) {
        layers.push(getLocationHighlight(highlightPoints));
    }

    layers.push(facilitiesLayer, aisLayer);

    if (routeData) {
        layers.push(...getRouteLayer(routeData, darkMode))
    }

    const dummyFilterState = { species: routeInput.voyage.preferredSpecies[0] ?? Species(s => s)[0].value, daysAhead: 1, showAISLayer: true };
    const t = useTranslation();
    const tooltip = (info: PickInfo<any>) => getTooltip(t, info, dummyFilterState);

    // Cleanup route-input, because we lose selected vessel and fishery state when switching away from route-planner
    // And we can't reverse engineer it from the from-to position data
    const location = useLocation();
    useEffect(() => {
        if (location.pathname.includes("route")) {
            return () => {
                setRouteInput(defaultRouteInput);
            };
        }
    }, [location.pathname, setRouteInput]);

    const menus = <>
        <RouteMenu aisVessels={aisVessels} routeInput={routeInput} setRouteInput={setRouteInput} setRouteData={setRouteData} />
    </>;

    return [
        layers,
        menus,
        tooltip
    ];
}
